// src/shared/components/NavBar.jsx
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

const ACTIVE_GREEN = "#20BC71";

export default function NavBar() {
    const { pathname } = useLocation();
    const isHome = pathname === "/";
    const [showBrand, setShowBrand] = useState(!isHome); // en Home inicia oculto
    const observerRef = useRef(null);

    const [mobileOpen, setMobileOpen] = useState(false);
    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const navItems = [
        { label: "Home", path: "/" },
        { label: "Analyze", path: "/analyze" },
        { label: "Browse", path: "/browse" },
        { label: "Download", path: "/download" },
    ];

    useEffect(() => {
        // En rutas que no son Home, mostramos siempre el título del navbar
        if (!isHome) {
            setShowBrand(true);
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            return;
        }

        // En Home: observar el título de la página
        const target = document.getElementById("home-page-title");
        if (!target) {
            setShowBrand(true);
            return;
        }

        const navHeight = 90; // altura de tu AppBar
        const obs = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                setShowBrand(!entry.isIntersecting);
            },
            {
                root: null,
                rootMargin: `-${navHeight}px 0px 0px 0px`,
                threshold: 0,
            }
        );

        obs.observe(target);
        observerRef.current = obs;
        return () => {
            obs.disconnect();
            observerRef.current = null;
        };
    }, [isHome]);

    // Drawer SIN título, negro, con subrayado verde en activo
    const drawer = (
        <Box
            onClick={handleDrawerToggle}
            sx={{
                height: "100%",
                bgcolor: "#000",
                color: "#fff",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <List sx={{ py: 1 }}>
                {navItems.map((item) => (
                    <ListItem key={item.path} disablePadding>
                        <ListItemButton
                            component={NavLink}
                            to={item.path}
                            sx={{
                                color: "inherit",
                                py: 1.5,
                                position: "relative",
                                "&:hover": { bgcolor: "#111" },
                                "&.active::after": {
                                    content: '""',
                                    position: "absolute",
                                    left: 16,
                                    right: 16,
                                    bottom: 6,
                                    height: "3px",
                                    backgroundColor: ACTIVE_GREEN,
                                    borderRadius: 2,
                                },
                            }}
                        >
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    borderBottom: "2px solid black",
                    bgcolor: "#000",
                    color: "#fff",
                    "--nav-h": "90px",
                }}
            >
                <Toolbar
                    disableGutters
                    sx={{
                        minHeight: 90,
                        px: { xs: 2, md: "var(--page-pad, 0px)" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    {/* === BLOQUE TIPOGRAFÍA SIN CAMBIOS DE ESTILO === */}
                    <Typography
                        variant="h5"
                        component={NavLink}
                        to="/"
                        sx={{
                            flexGrow: 1,
                            textDecoration: "none",
                            color: "inherit",
                            "&:hover": { opacity: 0.85 },

                            // Animación sutil de entrada/salida
                            opacity: showBrand ? 1 : 0,
                            transform: showBrand ? "translateY(0)" : "translateY(-8px)",
                            transition: "opacity 600ms ease, transform 600ms ease",
                            pointerEvents: showBrand ? "auto" : "none",
                        }}
                        aria-hidden={!showBrand}
                    >
                        Latin America Resistance Atlas
                    </Typography>

                    {/* Botones (desktop) — mismo estilo, solo subrayado verde en activo */}
                    <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
                        {navItems.map((item) => (
                            <Button
                                key={item.path}
                                component={NavLink}
                                to={item.path}
                                color="inherit"
                                sx={{
                                    position: "relative",
                                    borderRadius: 0,
                                    "&.active::after": {
                                        content: '""',
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "3px",
                                        backgroundColor: ACTIVE_GREEN,
                                    },
                                    "&:hover": { opacity: 0.8 },
                                }}
                            >
                                {item.label}
                            </Button>
                        ))}
                    </Box>

                    {/* Menú hamburguesa (móvil) */}
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="end"
                        onClick={() => setMobileOpen(true)}
                        sx={{ display: { xs: "flex", md: "none" } }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Drawer negro, sin título */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: "block", md: "none" },
                    "& .MuiDrawer-paper": {
                        boxSizing: "border-box",
                        width: 260,
                        bgcolor: "#000",
                        color: "#fff",
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
}
